import type {
  ConversationChannel,
  ConversationContext,
} from '@stripe-internal/conversations-client';

import type {CanonicalOverviewState} from './canonicalOverviewState';
import type {ConversationOverview} from './types';
import type {TargetChannel} from './ThreadsConversationClient';

export const isConversationLive = (
  overview?: ConversationOverview | null,
): boolean =>
  overview == null ||
  overview.is_active ||
  overview.live_chat?.status === 'active' ||
  overview.request_a_call?.status === 'queued';

export const isConversationOverviewEnded = (
  overview?: ConversationOverview | null,
): boolean => overview != null && !isConversationLive(overview);

// Rank only query results; history's pinned row is not a resume candidate.
export function selectResumableConversation(
  overviews: ConversationOverview[],
): ConversationOverview | undefined {
  const recent = [...overviews].sort(
    (a, b) => b.last_updated_at - a.last_updated_at,
  );
  return (
    recent.find((overview) => overview.live_chat?.status === 'active') ??
    recent.find((overview) => overview.request_a_call?.status === 'queued') ??
    recent.find((overview) => overview.is_active)
  );
}

export function resolveTargetChannel(
  overviewState: CanonicalOverviewState | undefined,
  context: ConversationContext,
): TargetChannel | null {
  const chat = overviewState?.overview?.live_chat;
  if (chat?.status === 'active') {
    // A historical live-chat snapshot can only resume a server-named session.
    return chat.external_id
      ? {channel: 'chat', chatId: chat.external_id}
      : null;
  }
  if (chat?.status === 'ended') {
    return null;
  }
  const chatId = context.chat_conversationId;
  if (context.channel === 'chat') {
    return typeof chatId === 'string' && chatId
      ? {channel: 'chat', chatId}
      : {channel: 'chat', create: true};
  }
  return {channel: 'bot'};
}

export function shouldConnectMessaging(
  overview: ConversationOverview | null | undefined,
  channel: ConversationChannel,
): boolean {
  if (!overview) {
    return true;
  }
  return channel === 'chat'
    ? overview.live_chat?.status === 'active'
    : overview.is_active;
}
